import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapPin, Home, Box, Camera, Search as SearchIcon, X, SlidersHorizontal, Eye } from 'lucide-react';
import LoopVideo, { filmSrc } from './elements/loopVideo.jsx';
import ListingPage from './elements/listingPage.jsx';
import { searchDesigns } from './elements/designsApi.jsx';
import { ALL_PROVINCES } from './elements/provinces.jsx';
import { fa, priceLine, timeAgo, listingTitle, shortViews } from './elements/listingFormat.jsx';
import './search.scss';

// تبِ «جست‌وجو»: همه‌ی آگهی‌های منتشرشده (جدیدترین اول)، با فیلترهای پنلِ «فیلتر» (نوارِ بالا) و استانِ نوارِ بالا.
// تصویرِ هر آگهی همان نمای سه‌بعدی (فیلمِ کوتاه) است؛ لمس = صفحه‌ی کاملِ آگهی (عکس‌ها، مشخصات، تماس، ورود به خانه)
// filters: خروجیِ SearchFilter ({ dealType, filters }) یا null

const ROOM_MAP = { '۱': 1, '۲': 2, '۳': 3, '۴': 4, '۵+': 5 };
const SORTS = [
  { id: 'new', label: 'جدیدترین' },
  { id: 'price_asc', label: 'ارزان‌ترین' },
  { id: 'price_desc', label: 'گران‌ترین' },
  { id: 'area_desc', label: 'بزرگ‌ترین' },
];

// فیلترهای پنل → پارامترهای سرور
export function regionParam(geo) {
  const g = geo && (geo.geometry || geo);
  const ring = g && g.type === 'Polygon' && g.coordinates && g.coordinates[0];
  if (!ring || ring.length < 4) return undefined;
  return ring
    .slice(0, -1)
    .map(([lng, lat]) => `${lat.toFixed(5)},${lng.toFixed(5)}`)
    .join(';');
}

export function filterParams(applied) {
  if (!applied) return {};
  const f = applied.filters || {};
  const p = { deal: applied.dealType };
  if (applied.dealType === 'sale') {
    p.priceMin = f.priceFrom;
    p.priceMax = f.priceTo;
    p.floorMin = f.floor && f.floor.from;
    p.floorMax = f.floor && f.floor.to;
  } else {
    p.depositMin = f.depositFrom;
    p.depositMax = f.depositTo;
    p.rentMin = f.rentFrom;
    p.rentMax = f.rentTo;
  }
  p.areaMin = f.area && f.area.from;
  p.areaMax = f.area && f.area.to;
  p.ageMin = f.buildYear && f.buildYear.from;
  p.ageMax = f.buildYear && f.buildYear.to;
  p.rooms = (f.rooms || []).map((r) => ROOM_MAP[r]).filter(Boolean);
  p.amen = [f.hasElevator && 'elevator', f.hasParking && 'parking', f.hasStorage && 'storage'].filter(Boolean);
  // محدوده‌ی خط‌کشی‌شده روی نقشه: «lat,lng;lat,lng;…» (۵ رقمِ اعشار ≈ ۱ متر). سرور فعلاً نادیده می‌گیرد —
  // بعداً آگهی‌های داخلِ همین چندضلعی نشان داده می‌شوند
  p.region = regionParam(f.region);
  return p;
}

// تعدادِ فیلترهای فعال (برای نشانِ «فیلترها»)
export function activeCount(params) {
  return Object.entries(params).filter(([k, v]) => k !== 'deal' && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length)).length + (params.deal ? 1 : 0);
}

function ResultCard({ design, onOpen }) {
  const l = design.listing || {};
  const photos = (l.photoUrls || []).length;
  const meta = [l.area && `${fa(l.area)} متر`, l.rooms > 0 && `${fa(l.rooms)} اتاق`, l.buildYear > 0 && `${fa(l.buildYear)} سال ساخت`]
    .filter(Boolean)
    .join(' • ');
  return (
    <button type="button" className="search-page__card" onClick={onOpen}>
      <div className="search-page__card-body">
        <h3>{listingTitle(design)}</h3>
        {meta && <p className="search-page__meta">{meta}</p>}
        <p className="search-page__price">{priceLine(l)}</p>
        <div className="search-page__footer">
          <span className="search-page__time">
            {timeAgo(design.createdAt)}
            {l.province ? ` در ${l.province}` : ''}
          </span>
          <span className="search-page__views">
            <Eye size={12} strokeWidth={2} />
            {shortViews(design.views)}
          </span>
        </div>
      </div>
      <div className="search-page__thumb">
        {design.filmUrl ? (
          <LoopVideo src={filmSrc(design, true)} poster={design.thumbUrl} threshold={0.6} />
        ) : design.thumbUrl ? (
          <img src={design.thumbUrl} alt="" loading="lazy" />
        ) : (
          <Home size={26} strokeWidth={1.4} />
        )}
        {design.modelUrl !== null && (
          <span className="search-page__3d">
            <Box size={11} strokeWidth={2.2} /> سه‌بعدی
          </span>
        )}
        {photos > 0 && (
          <span className="search-page__photos">
            <Camera size={11} strokeWidth={2.2} /> {fa(photos)}
          </span>
        )}
      </div>
    </button>
  );
}

function SearchPage({ province, applied, onOpenFilter, onClearFilter }) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('new');
  const [state, setState] = useState({ items: [], next: 0, total: null, loading: true, error: '' });
  const [open, setOpen] = useState(null);
  const loadingRef = useRef(false);
  const reqId = useRef(0);
  const sentinelRef = useRef(null);

  const params = { ...filterParams(applied), province: province && province !== ALL_PROVINCES ? province : '', q: query, sort };
  const key = JSON.stringify(params);

  const load = useCallback(
    async (offset) => {
      const my = ++reqId.current;
      loadingRef.current = true;
      setState((s) => ({ ...s, loading: true, error: '', ...(offset === 0 ? { items: [], total: null } : {}) }));
      try {
        const r = await searchDesigns(JSON.parse(key), offset);
        if (my !== reqId.current) return;
        setState((s) => {
          const have = new Set(offset === 0 ? [] : s.items.map((x) => x.id));
          const items = [...(offset === 0 ? [] : s.items), ...(r.items || []).filter((x) => !have.has(x.id))];
          return { items, next: r.next, total: r.total, loading: false, error: '' };
        });
      } catch (e) {
        if (my === reqId.current) setState((s) => ({ ...s, loading: false, error: e.message }));
      } finally {
        if (my === reqId.current) loadingRef.current = false;
      }
    },
    [key]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  // جست‌وجوی متنی: کمی بعد از تمام شدنِ تایپ
  useEffect(() => {
    const t = setTimeout(() => setQuery(text.trim()), 450);
    return () => clearTimeout(t);
  }, [text]);

  // اسکرولِ بی‌پایان
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !loadingRef.current && state.next != null) load(state.next);
      },
      { rootMargin: '500px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [load, state.next]);

  const nFilters = activeCount(filterParams(applied));

  return (
    <div className="search-page">
      <div className="search-page__tools">
        <label className="search-page__box">
          <SearchIcon size={17} strokeWidth={1.9} />
          <input
            type="search"
            value={text}
            placeholder="جست‌وجو در آگهی‌ها (مثلاً سعادت‌آباد، نورگیر…)"
            onChange={(e) => setText(e.target.value)}
          />
          {text && (
            <button type="button" onClick={() => setText('')} aria-label="پاک کردن">
              <X size={15} strokeWidth={2} />
            </button>
          )}
        </label>
        <div className="search-page__chips">
          <button type="button" className={`search-page__chip ${nFilters ? 'is-on' : ''}`} onClick={onOpenFilter}>
            <SlidersHorizontal size={14} strokeWidth={2} />
            <span>{nFilters ? `فیلترها (${fa(nFilters)})` : 'فیلترها'}</span>
          </button>
          {nFilters > 0 && (
            <button type="button" className="search-page__chip" onClick={onClearFilter}>
              <X size={13} strokeWidth={2} />
              <span>حذف فیلترها</span>
            </button>
          )}
          {SORTS.map((s) => (
            <button key={s.id} type="button" className={`search-page__chip ${sort === s.id ? 'is-sort' : ''}`} onClick={() => setSort(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
        {state.total != null && (
          <p className="search-page__count">
            {state.total ? `${fa(state.total)} آگهی` : ''}
            {params.province ? ` در ${params.province}` : ' در همه‌ی ایران'}
          </p>
        )}
      </div>

      <section className="search-page__list">
        {state.items.map((d) => (
          <ResultCard key={d.id} design={d} onOpen={() => setOpen(d)} />
        ))}
        {state.loading &&
          Array.from({ length: state.items.length ? 2 : 5 }).map((_, i) => <div key={`s${i}`} className="search-page__card is-loading" />)}
      </section>

      {!state.loading && !state.error && state.items.length === 0 && (
        <div className="search-page__empty">
          <MapPin size={28} strokeWidth={1.3} />
          <p>آگهی‌ای با این مشخصات پیدا نشد.</p>
          {(nFilters > 0 || query) && (
            <button
              type="button"
              onClick={() => {
                setText('');
                onClearFilter();
              }}
            >
              حذفِ فیلترها و جست‌وجو
            </button>
          )}
        </div>
      )}
      {state.error && (
        <div className="search-page__empty">
          <p>{state.error}</p>
          <button type="button" onClick={() => load(state.items.length ? state.next || 0 : 0)}>
            تلاشِ دوباره
          </button>
        </div>
      )}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {open && <ListingPage design={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

export default SearchPage;
